"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Info } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useTranslation } from "react-i18next"
import axiosInstance from "@/api/axios"
import { useTheme } from "@/pages/contexts/ThemeContext"

type Season = "summer" | "winter" | "spring" | "autumn"

interface WasteCreatedProps {
    selectedSeason: Season
    bins: string[]
    timeRange: string
    onKgWasteUpdate?: (kg: number) => void
}

interface SeasonalWasteData {
    paper: number
    pmd: number
    residual: number
    organic: number
    total: number
    season?: string
}

interface EnvironmentalImpactData {
    total_co2_without_garby: number
    total_co2_with_garby: number
    co2_savings: number
    equivalents: {
        [key: string]: {
            value: number
            unit: string
            note: string
        }
    }
    period_start: string
    period_end: string
}

interface SelectedEquivalent {
    key: string
    value: number
    unit: string
    displayName: string
    tooltip: string
}

const WasteCreated: React.FC<WasteCreatedProps> = ({ selectedSeason, timeRange, bins, onKgWasteUpdate }) => {
    const { t } = useTranslation()
    const { theme } = useTheme()
    const isDarkMode = theme === "dark"

    const [kgWaste, setKgWaste] = useState<number>(0)
    const [selectedEquivalent, setSelectedEquivalent] = useState<SelectedEquivalent | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string>("")

    const getBackgroundColor = () => (isDarkMode ? "#100c0c" : "#ffffff")

    const equivalentDisplayMap: { [key: string]: { displayName: string; tooltip: string } } = {
        tree_sequestration: { displayName: t("trees"), tooltip: t("treeTooltip") },
        gasoline_consumed: { displayName: t("gallonsOfGasoline"), tooltip: t("gasolineTooltip") },
        average_car_drive: { displayName: t("milesOfCarDriving"), tooltip: t("carDriveTooltip") },
        water_bottles: { displayName: t("plasticWaterBottles"), tooltip: t("waterBottlesTooltip") },
        smartphone_charges: { displayName: t("smartphoneCharges"), tooltip: t("smartphoneChargesTooltip") },
        lorry_drive: { displayName: t("milesOfTruckDriving"), tooltip: t("lorryDriveTooltip") },
        home_electricity: { displayName: t("hoursOfHomeElectricity"), tooltip: t("homeElectricityTooltip") },
    }

    // Normalize timeRange for API call
const normalizeTimeRangeForAPI = (range: string) => {
    const cleaned = range.replace(/\s+/g, "").toLowerCase() // remove spaces and lowercase
    const map: Record<string, string> = {
        day: "Day",
        week: "Week",
        month: "Month",
        "3months": "3Month",
        "6months": "6Month",
        year: "Year",
    }
    return map[cleaned] || "Month"
}


    const fetchSeasonalData = async (season: Season) => {
        try {
            setLoading(true)
            setError("")

            // ✅ Fetch waste composition
            const wasteResponse = await axiosInstance.get("others_page_waste_composition", {
                params: {
                    season: season,
                    timeRange: normalizeTimeRangeForAPI(timeRange),
                    bins: bins.join(","),
                },
                paramsSerializer: { indexes: null },
            })

            const wasteData: SeasonalWasteData = wasteResponse.data
            const seasonWasteTotal = wasteData.paper + wasteData.pmd + wasteData.organic + wasteData.residual

            setKgWaste(seasonWasteTotal)

            if (onKgWasteUpdate) {
                onKgWasteUpdate(seasonWasteTotal)
            }

            // ✅ Fetch environmental impact
            const currentYear = new Date().getFullYear()
            const impactResponse = await axiosInstance.get("enviromental_impact", {
                params: {
                    period: normalizeTimeRangeForAPI(timeRange),
                    year: currentYear.toString(),
                    bins: bins.join(","),
                },
                paramsSerializer: { indexes: null },
            })

            const impactData: EnvironmentalImpactData = impactResponse.data

            const meaningfulEquivalents = Object.entries(impactData.equivalents).filter(
                ([, equiv]) => equiv.value > 0
            )

            if (meaningfulEquivalents.length > 0) {
                const randomIndex = Math.floor(Math.random() * meaningfulEquivalents.length)
                const [key, equivalent] = meaningfulEquivalents[randomIndex]
                const displayInfo = equivalentDisplayMap[key] || {
                    displayName: equivalent.unit,
                    tooltip: equivalent.note,
                }

                setSelectedEquivalent({
                    key,
                    value: Math.round(equivalent.value),
                    unit: equivalent.unit,
                    displayName: displayInfo.displayName,
                    tooltip: displayInfo.tooltip,
                })
            } else {
                setSelectedEquivalent({
                    key: "fallback",
                    value: Math.round(seasonWasteTotal / 70),
                    unit: t("washingMachines"),
                    displayName: t("washingMachines"),
                    tooltip: t("washingMachinesTooltip"),
                })
            }
        } catch (err: any) {
            console.error("Error fetching seasonal data:", err)
            setError(t("wasteCreatedError"))
            setKgWaste(457.8)
            if (onKgWasteUpdate) onKgWasteUpdate(457.8)

            setSelectedEquivalent({
                key: "fallback",
                value: 6,
                unit: t("washingMachines"),
                displayName: t("washingMachines"),
                tooltip: t("environmentalImpactDefault"),
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchSeasonalData(selectedSeason)
    }, [selectedSeason, bins, timeRange])

    const getComparisonText = (): string =>
        selectedEquivalent
            ? t("environmentalImpactComparable", {
                  value: selectedEquivalent.value.toLocaleString(),
                  displayName: selectedEquivalent.displayName,
              })
            : t("calculatingImpact")

    const getTooltipText = (): string => selectedEquivalent?.tooltip || t("environmentalImpactDefault")

    const cardClasses = `flex flex-col h-full`
    const cardStyle = { backgroundColor: getBackgroundColor() }
    const titleClasses = `text-base sm:text-lg font-medium mb-3 text-center ${isDarkMode ? "text-gray-100" : "text-gray-900"}`
    const textClasses = `text-xs leading-relaxed flex-1 break-words ${isDarkMode ? "text-gray-300" : "text-gray-600"}`
    const kgTextClasses = `text-base font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`
    const errorTextClasses = `text-sm px-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`
    const iconClasses = `h-3 w-3 cursor-pointer flex-shrink-0 mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`

    if (loading) {
        return (
            <Card className={cardClasses} style={cardStyle}>
                <CardContent className="flex-1 flex flex-col p-4 sm:p-6">
                    <h2 className={titleClasses}>{t("wasteCreatedTitle")}</h2>
                    <div className="flex-1 flex items-center justify-center">
                        <div
                            className={`animate-pulse w-20 h-20 rounded-full ${
                                isDarkMode ? "bg-gray-700" : "bg-gray-200"
                            }`}
                        ></div>
                    </div>
                </CardContent>
            </Card>
        )
    }

    if (error) {
        return (
            <Card className={cardClasses} style={cardStyle}>
                <CardContent className="flex-1 flex flex-col p-4 sm:p-6">
                    <h2 className={titleClasses}>{t("wasteCreatedTitle")}</h2>
                    <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4">
                        <p className="text-3xl font-bold text-red-500">{t("error")}</p>
                        <p className={errorTextClasses}>{error}</p>
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card className={cardClasses} style={cardStyle}>
            <CardContent className="flex-1 flex flex-col p-3 sm:p-4">
                <h2 className={titleClasses}>{t("wasteCreatedTitle")}</h2>

                <div className="flex-1 flex flex-col justify-center items-center text-center space-y-3">
                    <div className="flex flex-col items-center space-y-1">
                        <p className="text-3xl sm:text-4xl font-bold text-green-500 leading-none">
                            {kgWaste.toFixed(2)}
                        </p>
                        <p className={kgTextClasses}>KG</p>
                    </div>

                    <div className="flex items-start justify-center space-x-2 max-w-full px-1">
                        <p className={textClasses}>{getComparisonText()}</p>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Info className={iconClasses} />
                                </TooltipTrigger>
                                <TooltipContent
                                    className={`text-xs font-medium max-w-64 ${
                                        isDarkMode ? "bg-gray-700 text-gray-200 border-gray-600" : "bg-white text-gray-900 border-gray-200"
                                    }`}
                                    side="bottom"
                                >
                                    {getTooltipText()}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

export default WasteCreated
